import { useSelector } from 'react-redux';
import * as React from 'react';
import { useEffect, useState } from 'react';
import DisplayHelper from '../../helpers/display_helper';
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import { Accordion, AccordionSummary, FormControl, InputLabel, OutlinedInput, Select, TextField } from '@mui/material';
import withDataLoaded from '../with_data_loaded';
import PageSpinner from '../shared/page_spinner';
import { customerHoldTypesLoader } from '../../actions/data_loaders';
import LocationHelper from '../../helpers/location_helper';
import Actions from '../../actions/actions';
import DobFormatInput from '../shared/date_format_input';
import ExpirationHelper from '../../helpers/expiration_helper';
import moment from 'moment';

const CustomerHoldModal = ({ setHoldCategorySku, setAdditionalHoldDetails }) => {
  const holdTypes = useSelector((state) => state.customerHoldTypes);
  const [holdSku, setHoldSku] = useState('');
  const [details, setDetails] = useState();

  const holdSkuSelectLabel = 'Customer Hold Type*';

  useEffect(() => {
    setHoldCategorySku(holdSku);
    setAdditionalHoldDetails(details);
  }, [holdSku, details, setHoldCategorySku, setAdditionalHoldDetails]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <p style={{ marginBottom: '15px' }}>
        Are you sure you want to put this customer on hold?
        <br />
        <br />
        Taking this action will prevent all forward movement on this application, including funding and disbursements.
        The installer owner of this customer will be notified that this customer has been placed on hold.
      </p>
      <>
        <FormControl variant="outlined">
          <InputLabel htmlFor="holdTypeSelect">{holdSkuSelectLabel}</InputLabel>
          <Select
            native
            required
            id="qa-hold-type-select-input"
            defaultValue="invalid"
            onChange={(e) => setHoldSku(e.target.value)}
            style={{ marginBottom: '15px', backgroundColor: 'white' }}
            input={<OutlinedInput notched label={holdSkuSelectLabel} />}
            variant="outlined"
          >
            <option value="invalid" disabled>
              Please select a hold reason...
            </option>
            {holdTypes
              .filter((type) => type.holdType === 'manual')
              .map(({ sku, label }) => {
                return (
                  <option key={sku} value={sku}>
                    {label}
                  </option>
                );
              })}
          </Select>
        </FormControl>
        <TextField
          id="qa-customer-hold-notes-text-field"
          label="Additional Details"
          className="customer-hold-notes-input"
          variant="outlined"
          onChange={(e) => setDetails(e.target.value)}
          defaultValue={''}
          inputProps={{ maxLength: 1023 }}
        />
      </>
    </div>
  );
};
const CustomerHoldCancelModal = () => {
  const customer = useSelector((state) => state.customer);
  const customerHoldData = useSelector((state) => state.customerBannerData)?.filter(
    (entry) => entry?.tableName === 'quoting_customers' && entry?.columnName === 'hold_category_sku'
  );
  const customerHoldTypes = useSelector((state) => state.customerHoldTypes);
  const [isOpen, setIsOpen] = useState(false);

  const renderHistoryEntry = (entry) => {
    const isPlaced = entry?.to !== null;
    const holdType = customerHoldTypes?.filter((type) => type?.sku === entry?.to)?.[0];
    return (
      <div className={`history-content-container ${entry?.to}-content-container`}>
        <div className="history-content-entry">
          <b>
            {`Hold `} <span className="qa-history-type">{isPlaced ? 'Placed' : 'Removed'}</span>
            {` by `} <span className="qa-history-name">{entry?.userFullName}</span>
            {' on '}
            <span className="qa-history-date">{DisplayHelper.formatDateV2(entry?.changeDate)}</span>
          </b>
          {entry?.to && (
            <div>
              {`Category: `} <span className="qa-history-category">{holdType?.label}</span>
            </div>
          )}
          {entry?.reason && (
            <div>
              {`Additional Details: `} <span className="qa-history-additional-details">{entry?.reason}</span>
            </div>
          )}
        </div>
        <div className="history-content-entry-lock">{isPlaced ? <LockPersonOutlinedIcon /> : <LockOpenIcon />}</div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <p style={{ marginBottom: '15px' }}>
        This customer is currently on hold. Would you like to remove the hold?
        <br />
        <br />
        Taking this action wilI allow all forward movement on this application, including funding and disbursements.
        <br />
        <br />
        Please ensure Concert Risk/Compliance has signed off on this hold release prior to completing this action.
      </p>
      <a
        id="qa-customer-hold-history"
        href={
          LocationHelper.getUrlForSubdomain('switch') +
          `/customers/${customer.id}/history?ignoreDefaults=true&CustomerHold=true`
        }
        target="_blank"
        rel="noreferrer"
        style={{ fontWeight: 'bold' }}
      >
        View Customer Hold History
      </a>
      <Accordion id="qa-history-accordion" className="qa-history-accordion" expanded={isOpen}>
        <AccordionSummary
          id="qa-history-title"
          className="qa-history-title"
          expandIcon={<ExpandMoreIcon />}
          onClick={() => setIsOpen(!isOpen)}
        >
          <AccessAlarmIcon style={{ marginRight: '5px' }} /> Hold Details:
        </AccordionSummary>
        <div className="history-content">{customerHoldData?.map((entry) => renderHistoryEntry(entry))}</div>
      </Accordion>
    </div>
  );
};

const ExtendCustomerModal = () => {
  const customer = useSelector((redux) => redux.customer);
  const { expirationDate } = useSelector((redux) => redux.financingMethod);
  const [formState, setFormState] = useState({});

  const validations = async () => {
    const reasonError = !formState.reason ? 'Extension reason is required' : null;
    const formattedDate = moment(formState.date, 'MM/DD/YYYY').format('YYYY-MM-DD');
    const dateError = ExpirationHelper.isFarPastExpiration(formattedDate, expirationDate, true);

    setFormState({
      ...formState,
      dateError,
      reasonError,
    });

    return !dateError && !reasonError;
  };

  useEffect(() => {
    Actions.setModalDataEntryOnSubmit(async () => {
      if (await validations()) {
        try {
          await Actions.updateFinancingMethod({
            customerId: customer?.id,
            body: {
              expirationDate: moment(formState.date, 'MM/DD/YYYY').format('YYYY-MM-DD'),
              expirationDateExtensionReason: formState.reason,
            },
          });
          await Actions.setMessages(['Customer Successfully Extended']);
        } catch (e) {
          Actions.setErrorMessages(e.messages);
          return false;
        }
        // clean up state
        await Actions.setModalDataEntryOnSubmit(() => 'not yet set');
        return true;
      }
      return false;
    });
    // eslint-disable-next-line
  }, [formState, customer.id]);

  return (
    <div className="qa-extend-customer-modal">
      <p>Are you sure you want to extend this customer?</p>
      <TextField
        id="qa-extend-customer-date-text-field"
        label="Adjusted Expiration Date (MM/DD/YYYY)"
        className="extend-customer-input"
        variant="outlined"
        onChange={(e) =>
          setFormState({
            ...formState,
            date: e.target.value,
            dateError: null,
          })
        }
        error={!!formState.dateError}
        helperText={formState.dateError}
        required
        defaultValue={''}
        InputProps={{
          inputComponent: DobFormatInput,
        }}
      />

      <TextField
        id="qa-extend-customer-reason-text-field"
        label="Reason for Extension"
        className="extend-customer-input"
        variant="outlined"
        onChange={(e) =>
          setFormState({
            ...formState,
            reason: e.target.value,
            reasonError: null,
          })
        }
        error={!!formState.reasonError}
        helperText={formState.reasonError}
        required
        defaultValue={''}
      />
    </div>
  );
};

const CustomerHoldModalContent = withDataLoaded({
  WithData: CustomerHoldModal,
  WithoutData: PageSpinner,
  data: [customerHoldTypesLoader],
});

const CustomerHoldCancelModalContent = withDataLoaded({
  WithData: CustomerHoldCancelModal,
  WithoutData: PageSpinner,
  data: [customerHoldTypesLoader],
});

export { CustomerHoldModalContent, CustomerHoldCancelModalContent, ExtendCustomerModal };
